<template>
  <div>
    <page-header
      title="资产风险"
      :crumbs="['投后管理', '资产风险']"
      subtitle="投后营业额走势与资金回收情况"
    >
      <template #actions>
        <button class="btn" @click="exportCsv">导出 CSV</button>
      </template>
    </page-header>

    <!-- 筛选 -->
    <div class="filter-card">
      <div class="filter-bar">
        <div class="filter-inline">
          <label>品牌</label>
          <el-select v-model="selectedBrand" filterable placeholder="选择品牌" style="width: 170px" @change="onBrandChange">
            <el-option v-for="b in brandOptions" :key="b.brandCode" :label="b.brandName || b.brandCode" :value="b.brandCode" />
          </el-select>
        </div>
        <div class="filter-inline">
          <label>门店</label>
          <el-select v-model="selectedStores" multiple filterable collapse-tags collapse-tags-tooltip placeholder="选择门店" style="width: 230px">
            <el-option v-for="s in storeList" :key="s.storeEntCode" :label="s.storeName" :value="s.storeEntCode" />
          </el-select>
        </div>
        <div class="filter-inline">
          <label>协议编号</label>
          <el-select
            v-model="selectedAgreements"
            multiple
            filterable
            clearable
            collapse-tags
            collapse-tags-tooltip
            placeholder="选择协议"
            style="width: 230px"
            @change="fetchAll"
          >
            <el-option
              v-for="a in agreementList"
              :key="a.agreementNo"
              :label="a.agreementNo"
              :value="a.agreementNo"
            />
          </el-select>
        </div>
        <div class="filter-inline">
          <label>时间范围</label>
          <el-date-picker v-model="dateRange" type="daterange" value-format="YYYY-MM-DD" style="width: 240px" />
        </div>
        <div class="filter-inline">
          <label>粒度</label>
          <el-radio-group v-model="granularity">
            <el-radio-button label="day">按日</el-radio-button>
            <el-radio-button label="month">按月</el-radio-button>
          </el-radio-group>
        </div>
        <button class="btn btn-primary" @click="fetchAll">查询</button>
      </div>
    </div>

    <!-- 图表 -->
    <div class="chart-card">
      <div class="chart-card__head">
        <span class="card-title">营业额走势与 EVA</span>
        <span class="chart-legend">
          <span class="lg"><i style="background: #2C5BE8"></i>投后营业额</span>
          <span class="lg"><i style="background: #B9CDFB"></i>投前营业额预估</span>
          <span class="lg"><i style="background: #E8912A"></i>EVA(%)</span>
        </span>
      </div>
      <div ref="trendChart" class="chart-canvas"></div>
    </div>

    <!-- 合计 -->
    <div class="summary-bar">
      <div class="summary-item">
        <div class="summary-label">应收金额合计</div>
        <div class="summary-value num">{{ totals.totalReceivableAmount || 0 }}</div>
      </div>
      <div class="summary-item">
        <div class="summary-label">欠款金额合计</div>
        <div class="summary-value num danger-text">{{ totals.totalArrearsAmount || 0 }}</div>
      </div>
      <div class="summary-item">
        <div class="summary-label">营业额合计</div>
        <div class="summary-value num">{{ totals.totalTurnover || 0 }}</div>
      </div>
    </div>

    <!-- 明细 -->
    <DataTable
      :columns="columns"
      :data="recoveryRows"
      :total="0"
      row-key="index"
      empty-text="暂无回收数据，请调整筛选条件后查询"
      @refresh="fetchAll"
    >
      <template #toolbar-left>
        <span class="dt-hint">投后资金回收情况 · 共 {{ recoveryRows.length }} 行</span>
      </template>
    </DataTable>
  </div>
</template>

<script setup>
import { ref, onMounted, watch, nextTick } from 'vue'
import { ElMessage } from 'element-plus'
import * as echarts from 'echarts'
import PageHeader from '@/components/PageHeader.vue'
import DataTable from '@/components/DataTable.vue'
import postInvestService from '@/services/postInvestService'
import assetService from '@/services/assetService'

const selectedBrand = ref('')
const brandOptions = ref([{ brandCode: '', brandName: '全部品牌' }])
const storeList = ref([])
const selectedStores = ref([])
const agreementList = ref([])
const selectedAgreements = ref([])
const dateRange = ref([])
const granularity = ref('day')
const trendChart = ref(null)
const recoveryRows = ref([])
const totals = ref({ totalReceivableAmount: 0, totalArrearsAmount: 0, totalTurnover: 0 })

const columns = [
  { prop: 'index', label: '序号', width: 70 },
  { prop: 'agreementNo', label: '协议编号', minWidth: 160 },
  { prop: 'storeName', label: '门店名称', minWidth: 170 },
  { prop: 'time', label: '时间', minWidth: 130 },
  { prop: 'receivableAmount', label: '应收金额', minWidth: 130, type: 'money' },
  { prop: 'arrearsAmount', label: '欠款金额', minWidth: 130, type: 'money' },
  { prop: 'turnover', label: '营业额', minWidth: 130, type: 'money' }
]

const fmtDate = (d) => {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const dd = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${dd}`
}

const setDefaultDateRange = () => {
  const end = new Date()
  const start = new Date()
  start.setDate(end.getDate() - 7)
  end.setDate(end.getDate() - 1)
  dateRange.value = [fmtDate(start), fmtDate(end)]
}

const fetchBrandList = async () => {
  try {
    const res = await assetService.getBrandList({})
    const list = Array.isArray(res?.data) ? res.data : (Array.isArray(res?.data?.list) ? res.data.list : [])
    brandOptions.value = [{ brandCode: '', brandName: '全部品牌' }, ...list]
  } catch (e) {
    ElMessage.error(e?.message || '获取品牌失败')
  }
}

const fetchStoreList = async () => {
  try {
    const params = selectedBrand.value ? { brandCode: selectedBrand.value } : {}
    const res = await assetService.getStoreListByBrandCode(params)
    storeList.value = Array.isArray(res?.data) ? res.data : (Array.isArray(res?.data?.list) ? res.data.list : [])
    selectedStores.value = []
  } catch (e) {
    ElMessage.error(e?.message || '获取门店失败')
  }
}

// 协议下拉与门店同源：不选品牌加载全部，选了品牌按品牌加载
const fetchAgreementList = async () => {
  try {
    const params = selectedBrand.value ? { brandCode: selectedBrand.value } : {}
    const res = await assetService.getAgreementListByBrandCode(params)
    agreementList.value = Array.isArray(res?.data) ? res.data : (Array.isArray(res?.data?.list) ? res.data.list : [])
    selectedAgreements.value = []
  } catch (e) {
    ElMessage.error(e?.message || '获取协议列表失败')
  }
}

const onBrandChange = async () => {
  await fetchStoreList()
  await fetchAgreementList()
  await nextTick()
  await fetchAll()
}

const renderCombined = (el, postSeries, preSeries) => {
  const chart = echarts.getInstanceByDom(el) || echarts.init(el)
  const postAgg = {}
  const preAgg = {}
  postSeries.forEach(p => {
    const d = p.date
    postAgg[d] = (postAgg[d] || 0) + Number(p.value || 0)
  })
  preSeries.forEach(p => {
    const d = p.date
    preAgg[d] = (preAgg[d] || 0) + Number(p.value || 0)
  })
  const dates = Array.from(new Set([...Object.keys(postAgg), ...Object.keys(preAgg)])).sort()
  const postY = dates.map(d => postAgg[d] || 0)
  const preY = dates.map(d => preAgg[d] || 0)
  const evaY = dates.map(d => {
    const pre = preAgg[d] || 0
    const post = postAgg[d] || 0
    return pre ? Number((((post - pre) / pre) * 100).toFixed(2)) : 0
  })
  chart.setOption({
    color: ['#2C5BE8', '#B9CDFB', '#E8912A'],
    tooltip: { trigger: 'axis' },
    legend: { show: false },
    grid: { left: 12, right: 12, top: 16, bottom: 8, containLabel: true },
    xAxis: { type: 'category', data: dates, axisLine: { lineStyle: { color: '#DCE0E8' } }, axisLabel: { color: '#8A90A2', fontSize: 11 } },
    yAxis: [
      { type: 'value', name: '金额', axisLabel: { color: '#8A90A2', fontSize: 11 }, splitLine: { lineStyle: { color: '#F5F6F8' } } },
      { type: 'value', name: 'EVA(%)', position: 'right', axisLabel: { color: '#8A90A2', fontSize: 11 }, splitLine: { show: false } }
    ],
    series: [
      { name: '投后营业额', type: 'line', smooth: true, showSymbol: false, yAxisIndex: 0, data: postY },
      { name: '投前营业额预估', type: 'line', smooth: true, showSymbol: false, yAxisIndex: 0, data: preY },
      { name: 'EVA(%)', type: 'bar', yAxisIndex: 1, data: evaY }
    ]
  })
}

const fetchAll = async () => {
  try {
    const params = {
      brandCode: selectedBrand.value,
      storeEntCodes: selectedStores.value,
      agreementNos: selectedAgreements.value,
      startDate: dateRange.value?.[0],
      endDate: dateRange.value?.[1],
      granularity: granularity.value
    }
    const [trend, rec] = await Promise.all([
      postInvestService.turnoverTrend(params),
      postInvestService.recoveryTable(params)
    ])
    renderCombined(trendChart.value, trend?.data?.postInvestSeries || [], trend?.data?.preInvestSeries || [])
    recoveryRows.value = rec?.data?.rows || []
    totals.value = {
      totalReceivableAmount: rec?.data?.totalReceivableAmount || 0,
      totalArrearsAmount: rec?.data?.totalArrearsAmount || 0,
      totalTurnover: rec?.data?.totalTurnover || 0
    }
  } catch (e) {
    ElMessage.error(e?.message || '查询失败')
  }
}

const exportCsv = () => {
  const header = ['序号', '协议编号', '门店名称', '时间', '应收金额', '欠款金额', '营业额']
  const rows = (recoveryRows.value || []).map(r =>
    [r.index, r.agreementNo, r.storeName, r.time, r.receivableAmount, r.arrearsAmount, r.turnover]
  )
  const content = [header, ...rows].map(r => r.join(',')).join('\n')
  const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  const brandName = selectedBrand.value
    ? (brandOptions.value.find(b => b.brandCode === selectedBrand.value)?.brandName || selectedBrand.value)
    : '全部品牌'
  // 协议编号可能含 / 等非法文件名字符，统一替换
  const ag = selectedAgreements.value.length
    ? '-' + selectedAgreements.value.join('+').replace(/[\\/:*?"<>|]/g, '_')
    : ''
  a.download = `${brandName}${ag}-${dateRange.value?.[0] || ''}-${dateRange.value?.[1] || ''}.csv`
  a.click()
  URL.revokeObjectURL(url)
}

watch(selectedStores, () => fetchAll())

onMounted(async () => {
  setDefaultDateRange()
  await fetchBrandList()
  await fetchStoreList()
  await fetchAgreementList()
  await fetchAll()
})
</script>

<style scoped>
.filter-card {
  background: var(--bg-card);
  border: 1px solid var(--border);
  border-radius: var(--radius-card);
  padding: 12px 16px;
  margin-bottom: 12px;
}

.filter-bar {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.filter-inline {
  display: flex;
  align-items: center;
  gap: 6px;
}

.filter-inline label {
  font-size: 12px;
  color: var(--text-secondary);
  white-space: nowrap;
}

.btn {
  padding: 6px 14px;
  border: 1px solid var(--border-strong);
  border-radius: var(--radius-control);
  background: var(--bg-card);
  color: var(--text-secondary);
  font-family: inherit;
  font-size: 13px;
  cursor: pointer;
}

.btn:hover {
  border-color: var(--brand-500);
  color: var(--brand-500);
}

.btn-primary {
  background: var(--brand-500);
  border-color: var(--brand-500);
  color: #fff;
}

.btn-primary:hover {
  background: var(--brand-600);
  border-color: var(--brand-600);
  color: #fff;
}

.chart-card {
  background: var(--bg-card);
  border: 1px solid var(--border);
  border-radius: var(--radius-card);
  padding: 14px 16px;
  margin-bottom: 12px;
}

.chart-card__head {
  display: flex;
  align-items: center;
  margin-bottom: 8px;
}

.card-title {
  font-size: 14px;
  font-weight: 500;
}

.chart-legend {
  margin-left: auto;
  display: flex;
  gap: 14px;
}

.lg {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 11px;
  color: var(--text-tertiary);
}

.lg i {
  width: 8px;
  height: 8px;
  border-radius: 2px;
}

.chart-canvas {
  height: 320px;
  width: 100%;
}

.summary-bar {
  display: flex;
  background: var(--bg-card);
  border: 1px solid var(--border);
  border-radius: var(--radius-card);
  padding: 13px 18px;
  margin-bottom: 12px;
}

.summary-item {
  flex: 1;
}

.summary-item + .summary-item {
  padding-left: 18px;
  border-left: 1px solid var(--border);
}

.summary-label {
  font-size: 11px;
  color: var(--text-tertiary);
}

.summary-value {
  font-size: 18px;
  font-weight: 500;
  margin-top: 3px;
}

.dt-hint {
  font-size: 12px;
  color: var(--text-secondary);
}
</style>
